// --------------------------------------------------------------
// Created On: 2025-09-30
// Author: Zachary Thomas
//
// Last Modified: 2025-11-07
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import Card from "../../../components/Card/Card";
import Error from "../../../components/Error/Error";
import PasswordRequirements from "../../../components/PasswordRequirements/PasswordRequirements";
import PasswordInput from "../../../components/PasswordInput/PasswordInput";
import styles from "./ChangeTemporaryPasswordForm.module.scss";

// Form for changing a user's temporary password and setting a permanent password.
export default function ChangeTemporaryPasswordForm(props: Props): Component {
  // Check if the user pressed enter, if they did we attempt to change password.
  function checkPressEnter(e: React.KeyboardEvent): void {
    if (e.key === "Enter") {
      props.onSubmit();
    }
  }

  return (
    <form className={styles.body} onSubmit={(e) => e.preventDefault()} onKeyDown={(e) => checkPressEnter(e)}>
      <Card title="Update Password">
        <div className="px-3 px-lg-5 mb-4">
          <p className="mt-2">
            The temporary password you entered will need to be replaced by a permanent password. Please enter your new
            password below.
          </p>

          <PasswordRequirements />

          <div className="mb-4">
            <label className="font-weight-bold mt-2">New Password</label>
            <PasswordInput
              password={props.newPassword}
              onChangePassword={(newPassword) => props.onChangeNewPassword(newPassword)}
            />
          </div>

          <div className="mb-4">
            <label className="font-weight-bold mt-2">Confirm New Password</label>
            <PasswordInput
              password={props.newConfirmationPassword}
              onChangePassword={(newConfirmationPassword) =>
                props.onChangeNewConfirmationPassword(newConfirmationPassword)
              }
            />
          </div>

          <Error message={props.errorMessage} />

          <div className="my-4">
            <div className={`${styles.btnContainer} pe-3 d-inline-block`}>
              <button
                data-test="cancel-password-update-button"
                type="submit"
                id="cancel-password-update"
                className="btn btn-secondary"
                disabled={props.disabled}
                onClick={() => props.onCancel()}
              >
                Return to Login
              </button>
            </div>

            <div className={`${styles.btnContainer} ps-3 d-inline-block float-end`}>
              <button
                data-test="password-update-button"
                type="submit"
                id="password-update"
                className="btn btn-success float-end"
                disabled={props.disabled}
                onClick={() => props.onSubmit()}
              >
                Update Password
              </button>
            </div>
          </div>
        </div>
      </Card>
    </form>
  );
}

interface Props {
  errorMessage: string;
  newPassword: string;
  newConfirmationPassword: string;
  disabled?: boolean;
  onChangeNewPassword: (newPassword: string) => void;
  onChangeNewConfirmationPassword: (newConfirmationPassword: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}
